import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import Checkbox from '../../../components/atoms/forms/Checkbox';
import StopBadge from '../../../components/atoms/StopBadge';
import StopStatusPill from '../../../components/atoms/StopStatusPill';
import {
  DISPATCH_SOURCE,
  STATUS_IDS,
  STATUS_META,
} from '../../../libs/stopStatus';
import { formatClockTime } from '../../../utils/displayFormat';
import { isDispatchSelectable, isUnreachableStatus } from './dispatchStops';
import { dispatchSubReasonLabel } from './dispatchReasons';

export const DISPATCH_ROW_COLUMNS = '32px 56px minmax(0, 1fr) auto';

const StyledDiv = styledComponent('div', {
  display: 'grid',
  gridTemplateColumns: DISPATCH_ROW_COLUMNS,
  alignItems: 'center',
  gap: theme.spacing.fieldGap,
  padding: theme.spacing.cellPadding,
  borderBottom: `1px solid ${theme.colors.borderLight}`,
  background: theme.colors.bgCard,

  '&:last-of-type': {
    borderBottom: 'none',
  },
  '&.is-locked': {
    background: theme.colors.bgMuted,
    color: theme.colors.textMuted,
  },
  '&.is-selected': {
    background: theme.colors.accentSoftBg,
  },

  '& .dsr-identity': {
    minWidth: 0,
    overflow: 'hidden',
  },
  '& .dsr-name': {
    fontSize: theme.typography.body.fontSize,
    color: theme.colors.textDark,
    fontWeight: theme.fontWeights.semibold,
    overflowWrap: 'anywhere',
    lineHeight: theme.lineHeights.body,
  },
  '&.is-locked .dsr-name': {
    color: theme.colors.textMuted,
  },
  '& .dsr-meta': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    overflowWrap: 'anywhere',
  },
  '& .dsr-status': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: theme.spacing.gapMd,
    minWidth: 0,
    justifySelf: 'end',
    textAlign: 'right',
  },
  '& .dsr-attribution': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
  },
});

function isDispatchCoded(unreachable) {
  return Boolean(unreachable && unreachable.source === DISPATCH_SOURCE);
}

function attributionFor({ status, driver, stockedAt, unreachable }) {
  if (isUnreachableStatus(status)) {
    const parts = [];
    if (isDispatchCoded(unreachable)) {
      parts.push(`Dispatch · ${dispatchSubReasonLabel(unreachable.subReason)}`);
    }
    if (driver) parts.push(`by ${driver}`);
    if (isDispatchCoded(unreachable) && unreachable.createdAt) {
      parts.push(formatClockTime(new Date(unreachable.createdAt)));
    }
    return parts.join(' · ');
  }
  if (status === STATUS_IDS.COMPLETED) {
    return stockedAt ? `Stocked ${stockedAt}` : '';
  }
  return driver ? `by ${driver}` : '';
}

function DispatchStopRow({ stop, stopKey, checked, disabled, onToggle }) {
  const derivedStatus = stop.derivedStatus || {
    status: STATUS_IDS.NOT_STARTED,
  };
  const meta =
    STATUS_META[derivedStatus.status] || STATUS_META[STATUS_IDS.NOT_STARTED];
  const selectable = isDispatchSelectable(stop);
  const name = stop.displayName || stop.id || 'Unnamed stop';
  const showId = Boolean(stop.displayName) && stop.displayName !== stop.id;
  const attribution = attributionFor(derivedStatus);
  const attributionTitle =
    isDispatchCoded(derivedStatus.unreachable) &&
    derivedStatus.unreachable.notes
      ? derivedStatus.unreachable.notes
      : undefined;
  const className = [!selectable && 'is-locked', checked && 'is-selected']
    .filter(Boolean)
    .join(' ');

  return (
    <StyledDiv
      className={className}
      data-testid={`dispatch-stop-row-${stopKey}`}
    >
      <Checkbox
        checked={checked}
        disabled={disabled || !selectable}
        onChange={(next) => onToggle(stopKey, next)}
        aria-label={`Select ${name} for the dispatch update`}
        dataTestId={`dispatch-stop-checkbox-${stopKey}`}
      />
      <StopBadge tone={selectable ? 'success' : 'muted'}>{stop.stop}</StopBadge>
      <div className="dsr-identity">
        <div className="dsr-name" title={name}>
          {name}
        </div>
        {showId && (
          <div className="dsr-meta" title={stop.id}>
            {stop.id}
          </div>
        )}
      </div>
      <div className="dsr-status">
        <StopStatusPill
          tone={meta.tone}
          label={meta.label}
          locked={!selectable}
          tooltip={meta.description}
        />
        {attribution && (
          <span
            className="dsr-attribution"
            data-testid={`dispatch-stop-attribution-${stopKey}`}
            title={attributionTitle}
          >
            {attribution}
          </span>
        )}
      </div>
    </StyledDiv>
  );
}

DispatchStopRow.propTypes = {
  stop: PropTypes.shape({
    id: PropTypes.string,
    stop: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    displayName: PropTypes.string,
    derivedStatus: PropTypes.shape({
      status: PropTypes.string,
      driver: PropTypes.string,
      stockedAt: PropTypes.string,
      unreachable: PropTypes.shape({
        reason: PropTypes.string,
        subReason: PropTypes.string,
        source: PropTypes.string,
        notes: PropTypes.string,
        createdAt: PropTypes.string,
      }),
    }),
  }).isRequired,
  stopKey: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

DispatchStopRow.defaultProps = {
  checked: false,
  disabled: false,
};

export default DispatchStopRow;
